import { useEffect } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { AdsAccountsTab } from "@/components/admin/ads/ads-accounts-tab";
import { AdsCampaignsTab } from "@/components/admin/ads/ads-campaigns-tab";
import { AdsConversionsTab } from "@/components/admin/ads/ads-conversions-tab";
import { AdsGuardrailsTab } from "@/components/admin/ads/ads-guardrails-tab";
import {
  AdsOverviewTab,
  RANGE_PRESETS,
  type RangePreset,
} from "@/components/admin/ads/ads-overview-tab";
import { AD_CONVERSION_STATUSES, type AdConversionStatus } from "@/components/admin/ads/types";
import { ADS_QUERY_PREFIX } from "@/components/admin/ads/use-ad-connections";
import { LiveSwitchCard, type LiveEnvelope } from "@/components/admin/marketing/live-switch-card";
import {
  adsLiveQueryKey,
  adsLiveQueryOptions,
} from "@/components/admin/marketing/readiness/live-queries";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminContext } from "@/lib/admin-context";
import { showToast } from "@/lib/ui/toast";

/**
 * Admin → Marketing → Ads.
 *
 * Read-only paid-ads console with two narrow write paths, both behind the
 * "ads live" switch at the top of the page:
 *   - server-side B2B conversions (claimed profile, first paid subscription),
 *   - the pause-only budget circuit breaker.
 * Nothing here creates, edits or resumes a campaign on Meta or Google.
 * Spec: `_docs/marketing-automation-spec.md` §1, §3 WP-B / WP-C / WP-E2.
 *
 * Five URL-driven tabs (validateSearch + isValidTab + navigate replace, like
 * marketing-social.tsx), so every view is linkable:
 *
 *   /marketing-ads?tab=conversions&status=failed
 *   /marketing-ads?tab=campaigns&connection=<id>
 *
 * The Google OAuth callback lands on `?tab=accounts&connected=<id>` (or
 * `&error=<code>`), plus `&warning=scopes_missing` when a scope — the Data
 * Manager one, which carries conversion uploads — was unticked on Google's
 * consent screen. The page toasts once and rewrites `connected` into
 * `highlight`, which rings the card without re-toasting on reload.
 *
 * Endpoints (prefix /api/admin/ads): GET|PUT /live, GET /overview,
 * GET|POST /connections, PATCH|DELETE /connections/:id, POST
 * /connections/:id/{test,sync}, PATCH /connections/:id/guardrails, GET
 * /connections/:id/google/connect, GET /campaigns, GET /conversions,
 * POST /conversions/:id/retry.
 *
 * Every query carries `dbTarget` in its key, so the Local↔Prod admin toggle
 * refetches instead of serving the other database's rows.
 */

type AdsTab = "overview" | "accounts" | "campaigns" | "conversions" | "guardrails";

const VALID_TABS: AdsTab[] = ["overview", "accounts", "campaigns", "conversions", "guardrails"];

interface AdsSearch {
  tab?: AdsTab;
  range?: RangePreset;
  /** Campaigns tab: filter by connection id. */
  connection?: string;
  /** Conversions tab: status filter + paging. */
  status?: AdConversionStatus;
  offset?: number;
  /** Google OAuth callback: the connection that was just authorised. */
  connected?: string;
  /** Google OAuth callback: error code. */
  error?: string;
  /** Google OAuth callback: connected, but with a caveat (`scopes_missing`). */
  warning?: string;
  /** Connection card to ring (set from `connected` after the toast). */
  highlight?: string;
}

function isValidTab(value: string): value is AdsTab {
  return (VALID_TABS as string[]).includes(value);
}

function asId(value: unknown): string | undefined {
  return typeof value === "string" && /^[A-Za-z0-9_-]{1,64}$/.test(value.trim())
    ? value.trim()
    : undefined;
}

function asRange(value: unknown): RangePreset | undefined {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return (RANGE_PRESETS as readonly number[]).includes(n) ? (n as RangePreset) : undefined;
}

function asOffset(value: unknown): number | undefined {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
}

export const Route = createFileRoute("/_authenticated/marketing-ads")({
  validateSearch: (search: Record<string, unknown>): AdsSearch => {
    const rawTab = typeof search.tab === "string" ? search.tab : undefined;
    const rawStatus = typeof search.status === "string" ? search.status : undefined;
    return {
      tab: rawTab && isValidTab(rawTab) ? rawTab : undefined,
      range: asRange(search.range),
      connection: asId(search.connection),
      status:
        rawStatus && (AD_CONVERSION_STATUSES as readonly string[]).includes(rawStatus)
          ? (rawStatus as AdConversionStatus)
          : undefined,
      offset: asOffset(search.offset),
      connected: asId(search.connected),
      error: asId(search.error),
      warning: asId(search.warning),
      highlight: asId(search.highlight),
    };
  },
  component: MarketingAdsPage,
});

function MarketingAdsPage() {
  const { t } = useLingui();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { dbTarget } = useAdminContext();

  const activeTab: AdsTab = search.tab ?? "overview";

  const patchSearch = (next: Partial<AdsSearch>) => {
    void navigate({
      search: (prev: AdsSearch) => ({ ...prev, ...next }),
      replace: true,
    });
  };

  // Google OAuth landing. Toast once, then rewrite the one-shot params so a
  // reload or a shared link does not announce the connection again.
  useEffect(() => {
    const connectedId = search.connected;
    const errorCode = search.error;
    const warningCode = search.warning;
    if (!connectedId && !errorCode && !warningCode) return;
    if (connectedId && warningCode === "scopes_missing") {
      showToast(
        t`Connected, but the Data Manager scope was not granted — conversion uploads will fail; reconnect and tick every scope`,
        "error",
      );
    } else if (connectedId) {
      showToast(t`Google Ads connected — spend syncs on the next hourly run`, "success");
    } else if (errorCode) {
      showToast(
        (() => {
          switch (errorCode) {
            case "access_denied":
              return t`Google Ads connection cancelled — access was not granted`;
            case "invalid_state":
            case "session_mismatch":
            case "missing_params":
              return t`The Google Ads connect link expired or was started in another session — try again`;
            case "not_configured":
              return t`Google Ads OAuth is not configured on the API`;
            case "token_exchange":
              return t`Google Ads authorisation failed — the account card shows why; connect again`;
            case "not_found":
              return t`That ad account no longer exists — refresh the list`;
            default:
              return t`Google Ads connection failed (${errorCode})`;
          }
        })(),
        "error",
      );
    }
    void queryClient.invalidateQueries({ queryKey: [...ADS_QUERY_PREFIX] });
    void navigate({
      search: (prev: AdsSearch) => ({
        ...prev,
        tab: "accounts",
        connected: undefined,
        error: undefined,
        warning: undefined,
        highlight: connectedId ?? prev.highlight,
      }),
      replace: true,
    });
  }, [search.connected, search.error, search.warning, navigate, queryClient, t]);

  const liveQuery = useQuery(adsLiveQueryOptions(dbTarget));

  const onLiveChanged = (next: LiveEnvelope) => {
    queryClient.setQueryData(adsLiveQueryKey(dbTarget), next);
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          <Trans>Ads</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>
            Meta and Google Ads spend next to what it bought — claimed profiles and paid
            subscriptions. Read-only, with a pause-only budget guard.
          </Trans>
        </p>
      </header>

      <LiveSwitchCard
        kind="ads"
        endpoint="/api/admin/ads/live"
        envelope={liveQuery.data ?? null}
        loading={liveQuery.isLoading}
        onChanged={onLiveChanged}
        description={
          <Trans>
            “Ads live” controls exactly two things: sending server-side conversions (profile
            claimed, first paid subscription) to Meta and Google, and the auto-pause circuit
            breaker. Spend and campaign sync run either way. Nothing here ever creates, edits or
            resumes a campaign.
          </Trans>
        }
      />

      <Tabs
        value={activeTab}
        onValueChange={(v) => patchSearch({ tab: v as AdsTab, highlight: undefined })}
      >
        <TabsList className="flex w-full flex-wrap justify-start sm:w-fit">
          <TabsTrigger value="overview" className="min-h-[44px] flex-none px-3">
            <Trans>Overview</Trans>
          </TabsTrigger>
          <TabsTrigger value="accounts" className="min-h-[44px] flex-none px-3">
            <Trans>Accounts</Trans>
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="min-h-[44px] flex-none px-3">
            <Trans>Campaigns</Trans>
          </TabsTrigger>
          <TabsTrigger value="conversions" className="min-h-[44px] flex-none px-3">
            <Trans>Conversions</Trans>
          </TabsTrigger>
          <TabsTrigger value="guardrails" className="min-h-[44px] flex-none px-3">
            <Trans>Guardrails</Trans>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="pt-4">
          <AdsOverviewTab
            range={search.range ?? 30}
            onRangeChange={(range) => patchSearch({ range })}
            onGoToAccounts={() => patchSearch({ tab: "accounts" })}
          />
        </TabsContent>

        <TabsContent value="accounts" className="pt-4">
          <AdsAccountsTab highlightId={search.highlight} />
        </TabsContent>

        <TabsContent value="campaigns" className="pt-4">
          <AdsCampaignsTab
            connectionId={search.connection ?? ""}
            onConnectionChange={(connection) => patchSearch({ connection })}
          />
        </TabsContent>

        <TabsContent value="conversions" className="pt-4">
          <AdsConversionsTab
            status={search.status}
            offset={search.offset ?? 0}
            onFilterChange={(next) => patchSearch(next)}
          />
        </TabsContent>

        <TabsContent value="guardrails" className="pt-4">
          <AdsGuardrailsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
